import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/CloseRounded'
import { useTranslation } from 'react-i18next'

import { getAiToggleConfigs, HeaderModalProps, ToggleSection } from './Helper'

const HeaderModal: React.FC<HeaderModalProps> = ({
  open,
  sentimentStatus,
  thematicStatus,
  onClose,
  onSave,
  sentimentAnalysis,
  thematicAnalysis,
}) => {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const [toggles, setToggles] = useState<Record<string, boolean>>({
    sentiment: !!sentimentStatus,
    thematic: !!thematicStatus,
  })

  useEffect(() => {
    setToggles({
      sentiment: !!sentimentStatus,
      thematic: !!thematicStatus,
    })
  }, [sentimentStatus, thematicStatus])

  const handleToggleChange = (key: string, checked: boolean) => {
    setToggles((prev) => ({ ...prev, [key]: checked }))
  }

  const toggleConfigs = getAiToggleConfigs(
    t,
    sentimentAnalysis,
    thematicAnalysis,
  )

  const handleSaveClick = async () => {
    setIsSaving(true)
    try {
      await onSave({
        sentiment: sentimentAnalysis ? toggles.sentiment : '0',
        themes_keyphrases: thematicAnalysis ? toggles.thematic : '0',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={onClose}
      fullWidth
      disablePortal
      data-testid="ai-settings-dialog"
      aria-labelledby="ai-settings-dialog-title"
    >
      <DialogTitle id="ai-settings-dialog-title">AI Settings</DialogTitle>
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{ position: 'absolute', right: 10, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers data-testid="ai-settings-dialog-content">
        {toggleConfigs.map((config) => (
          <ToggleSection
            key={config.keyName}
            keyName={config.keyName}
            label={config.label}
            helperText={config.helperText}
            analysisAvailable={config.analysisAvailable}
            onText={config.onText}
            offText={config.offText}
            enabled={toggles[config.keyName]}
            disabledText={t('textAnalysis.aiSettings.disabledText')}
            onChange={handleToggleChange}
          />
        ))}
        {(sentimentAnalysis || thematicAnalysis) && (
          <Alert severity="info" sx={{ py: 0 }}>
            Processing may take time. You can continue working.
          </Alert>
        )}
      </DialogContent>
      <DialogActions data-testid="ai-dialog-actions" sx={{ p: 2 }}>
        <Button
          onClick={handleSaveClick}
          color="primary"
          variant="contained"
          disableElevation
          disabled={isSaving || (!sentimentAnalysis && !thematicAnalysis)}
          data-testid="save-button"
          startIcon={
            isSaving ? (
              <CircularProgress
                size={20}
                color="inherit"
                data-testid="saving-spinner"
              />
            ) : null
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
export { HeaderModal }
